import { useRef, useState } from 'react';
import { PanelLeft } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { Sheet, SheetContent, SheetHeader, SheetTitle } from '@/components/ui/sheet';
import { Button } from '@/components/ui/button';
import { ApiError } from '@/lib/api';
import { useCreateConversation } from '@/hooks/mutations/useAiMutations';
import { ConversationList } from './ConversationList';
import { ChatThread } from './ChatThread';
import { ChatInput } from './ChatInput';
import { ChatWelcome } from './ChatWelcome';
import { ChatCreditsPill } from './ChatCreditsPill';
import { ChatMessage } from './ChatMessage';
import { ChatMessageBubble } from './ChatMessageBubble';
import { useIsMobile } from '@/hooks/useIsMobile';
import { cn } from '@/lib/utils';
import type { AiMessage, ChatUiMessage } from '@/types/ai';
import { ChatGlassHeader } from './ChatGlassHeader';
import { QUICK_COMMANDS } from './chatConstants';

function formatPanelError(error: unknown, fallback: string): string {
  if (error instanceof ApiError) {
    switch (error.code) {
      case 'INSUFFICIENT_CREDITS':
        return 'Crédits IA insuffisants. Achetez des crédits pour continuer.';
      case 'RATE_LIMIT_EXCEEDED':
        return "Limite de messages atteinte pour aujourd'hui.";
      case 'NO_SHOP':
        return 'Aucune boutique sélectionnée. Veuillez en sélectionner une.';
      case 'AI_API_ERROR':
        return "L'assistant IA est temporairement indisponible. Réessayez dans quelques instants.";
      default:
        if (error.message && error.message.length < 200) return error.message;
        return fallback;
    }
  }
  if (error instanceof Error) {
    if (error.message === 'Failed to fetch' || error.message.includes('NetworkError')) {
      return 'Connexion impossible. Vérifiez votre connexion internet.';
    }
    return error.message;
  }
  return fallback;
}

interface ChatPanelProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function ChatPanel({ open, onOpenChange }: ChatPanelProps) {
  const { t } = useTranslation('dashboard');
  const isMobile = useIsMobile();
  const [activeConversationId, setActiveConversationId] = useState<string | null>('__new__');
  const [historyOpen, setHistoryOpen] = useState(false);
  const [desktopHistoryOpen, setDesktopHistoryOpen] = useState(true);
  const [queuedPrompt, setQueuedPrompt] = useState<string | null>(null);
  const [pendingFirstMessage, setPendingFirstMessage] = useState<string | null>(null);
  const [initialMessages, setInitialMessages] = useState<AiMessage[] | null>(null);
  const createConversation = useCreateConversation();

  function handleNewConversation() {
    setActiveConversationId('__new__');
    setPendingFirstMessage(null);
    setInitialMessages(null);
    setHistoryOpen(false);
  }

  async function handleFirstMessage(message: string) {
    setPendingFirstMessage(message);
    try {
      const result = await createConversation.mutateAsync(message);

      const now = new Date().toISOString();
      const seedMessages: AiMessage[] = [
        { id: `seed-user-${Date.now()}`, role: 'user', content: message, created_at: now },
      ];
      if (result.assistant) {
        seedMessages.push(result.assistant);
      }
      setInitialMessages(seedMessages);
      setQueuedPrompt(result.assistant ? null : message);
      setPendingFirstMessage(null);
      setActiveConversationId(result.conversation.id);
    } catch {
      // Error is handled by mutation state — pendingFirstMessage stays visible with error.
    }
  }

  function handleDeletedConversation(id: string) {
    if (activeConversationId === id) {
      setActiveConversationId('__new__');
    }
  }

  function handleBack() {
    setActiveConversationId('__new__');
    setPendingFirstMessage(null);
    setInitialMessages(null);
  }

  function handleSelectConversation(id: string) {
    setActiveConversationId(id);
    setInitialMessages(null);
    setPendingFirstMessage(null);
    setHistoryOpen(false);
  }

  const isNewConversation = activeConversationId === null || activeConversationId === '__new__';

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent
        side="right"
        showCloseButton
        className="p-0 flex min-h-0 flex-col gap-0 w-full border-l border-neutral-300 sm:max-w-3xl lg:max-w-6xl"
      >
        <SheetHeader className="border-b border-slate-200/80 px-4 py-2.5 dark:border-charcoal-700 bg-[#f5f7f9] dark:bg-charcoal-900 shrink-0">
          <div className="flex items-center justify-between gap-3">
            <div className="flex items-center gap-3 min-w-0">
              <Button
                variant="ghost"
                size="icon"
                onClick={() => {
                  if (isMobile) setHistoryOpen((prev) => !prev);
                  else setDesktopHistoryOpen((prev) => !prev);
                }}
                aria-label={t('chat_history', { defaultValue: 'Historique' })}
                aria-pressed={desktopHistoryOpen}
                className="size-8 rounded-full text-slate-600 dark:text-slate-300 hover:bg-slate-200/60 dark:hover:bg-charcoal-800 shrink-0"
              >
                <PanelLeft className="size-4.5" />
              </Button>

              <div className="flex items-center gap-2.5 min-w-0">
                <div className="size-10 rounded-full bg-slate-200 overflow-hidden shrink-0 border border-slate-200 dark:border-charcoal-700 shadow-2xs flex items-center justify-center">
                  <img
                    src="https://images.unsplash.com/photo-1534528741775-53994a69daeb?w=100&auto=format&fit=crop&q=80"
                    alt="Velko Avatar"
                    className="size-full object-cover"
                    onError={(e) => {
                      (e.target as HTMLElement).style.display = 'none';
                    }}
                  />
                </div>
                <div className="flex flex-col text-left min-w-0">
                  <SheetTitle className="font-heading text-lg font-bold text-slate-900 dark:text-white leading-tight truncate">
                    Copilote Velko
                  </SheetTitle>
                  <span className="text-[10px] font-mono font-semibold uppercase tracking-widest text-slate-400 dark:text-slate-400 truncate">
                    ASSISTANT DE GESTION INTELLIGENT
                  </span>
                </div>
              </div>
            </div>

            <div className="flex items-center gap-2 pr-7 shrink-0">
              <ChatCreditsPill />
            </div>
          </div>
        </SheetHeader>

        <div className="flex-1 min-h-0 overflow-hidden">
          {isMobile ? (
            <div className="flex h-full min-h-0 flex-col">
              {isNewConversation ? (
                <NewConversationThread
                  onFirstMessage={(message) => void handleFirstMessage(message)}
                  onBack={handleBack}
                  isPending={createConversation.isPending}
                  error={createConversation.error}
                  pendingMessage={pendingFirstMessage}
                  showBackButton={false}
                  onOpenHistory={() => setHistoryOpen(true)}
                  hideHeader
                />
              ) : (
                <ChatThread
                  conversationId={activeConversationId}
                  onBack={handleBack}
                  showBackButton={false}
                  hideInternalHeader
                  queuedPrompt={queuedPrompt}
                  onQueuedPromptConsumed={() => setQueuedPrompt(null)}
                  onNewConversation={handleNewConversation}
                  initialMessages={initialMessages}
                  onInitialMessagesConsumed={() => setInitialMessages(null)}
                />
              )}

              <Sheet open={historyOpen} onOpenChange={setHistoryOpen}>
                <SheetContent
                  side="left"
                  className="w-full max-w-full sm:max-w-sm p-0 border-r border-neutral-200 dark:border-charcoal-700 bg-white dark:bg-charcoal-900"
                >
                  <ConversationList
                    selectedConversationId={isNewConversation ? null : activeConversationId}
                    onSelectConversation={handleSelectConversation}
                    onNewConversation={handleNewConversation}
                    onConversationDeleted={handleDeletedConversation}
                  />
                </SheetContent>
              </Sheet>
            </div>
          ) : (
            <div
              className={cn(
                'grid h-full min-h-0',
                desktopHistoryOpen ? 'grid-cols-[288px_minmax(0,1fr)]' : 'grid-cols-1',
              )}
            >
              {desktopHistoryOpen && (
                <ConversationList
                  selectedConversationId={activeConversationId}
                  onSelectConversation={handleSelectConversation}
                  onNewConversation={handleNewConversation}
                  onConversationDeleted={handleDeletedConversation}
                />
              )}

              {isNewConversation ? (
                <NewConversationThread
                  onFirstMessage={(message) => void handleFirstMessage(message)}
                  onBack={handleBack}
                  isPending={createConversation.isPending}
                  error={createConversation.error}
                  pendingMessage={pendingFirstMessage}
                  showBackButton={false}
                  hideHeader
                />
              ) : (
                <ChatThread
                  conversationId={activeConversationId}
                  onBack={handleBack}
                  showBackButton={false}
                  hideInternalHeader
                  queuedPrompt={queuedPrompt}
                  onQueuedPromptConsumed={() => setQueuedPrompt(null)}
                  initialMessages={initialMessages}
                  onInitialMessagesConsumed={() => setInitialMessages(null)}
                />
              )}
            </div>
          )}
        </div>
      </SheetContent>
    </Sheet>
  );
}

// ── New conversation thread ──────────────────────────────────────────────────
interface NewConversationThreadProps {
  onFirstMessage: (message: string) => void;
  onBack: () => void;
  isPending: boolean;
  error: unknown;
  pendingMessage?: string | null;
  showBackButton: boolean;
  onOpenHistory?: () => void;
  hideHeader?: boolean;
}

function NewConversationThread({
  onFirstMessage,
  onBack: _onBack,
  isPending,
  error,
  pendingMessage = null,
  showBackButton: _showBackButton,
  onOpenHistory,
  hideHeader = false,
}: NewConversationThreadProps) {
  const { t } = useTranslation('dashboard');
  const isMobile = useIsMobile();
  const bottomRef = useRef<HTMLDivElement>(null);

  return (
    <div className="flex h-full min-h-0 flex-col bg-slate-50 dark:bg-background">
      {!hideHeader && (
        <ChatGlassHeader
          title="Copilote Velko"
          subtitle="ASSISTANT DE GESTION INTELLIGENT"
          onOpenHistory={onOpenHistory}
        />
      )}

      {pendingMessage ? (
        <div className="flex flex-1 flex-col overflow-y-auto">
          <PendingFirstMessageView message={pendingMessage} isMobile={isMobile} error={error} />
          <div ref={bottomRef} />
        </div>
      ) : (
        <>
          {Boolean(error) && (
            <div className="mx-3 mt-3 rounded-md border border-error bg-error-light p-2 text-xs text-error-dark">
              {formatPanelError(error, t('chat_error'))}
            </div>
          )}
          <ChatWelcome onSelectPrompt={onFirstMessage} />
        </>
      )}

      <ChatInput
        onSend={onFirstMessage}
        disabled={isPending}
        draftKey="__new__"
        maxLength={8000}
        quickCommands={QUICK_COMMANDS}
        placeholder={pendingMessage ? t('chat_thinking') : t('chat_placeholder')}
        mobileCompact
      />
    </div>
  );
}

// ── Pending message view (optimistic feedback) ───────────────────────────────
function PendingFirstMessageView({
  message,
  isMobile = false,
  error,
}: {
  message: string;
  isMobile?: boolean;
  error?: unknown;
}) {
  const { t } = useTranslation('dashboard');
  const now = new Date().toISOString();
  const hasFailed = Boolean(error);

  const userMessage: ChatUiMessage = {
    id: 'pending-user',
    role: 'user',
    content: message,
    created_at: now,
    status: hasFailed ? 'failed' : 'done',
  };

  const assistantMessage: ChatUiMessage = {
    id: 'pending-assistant',
    role: 'assistant',
    content: '',
    created_at: now,
    status: hasFailed ? 'failed' : 'streaming',
    error_message: hasFailed ? formatPanelError(error, t('chat_error')) : undefined,
  };

  const MessageComponent = isMobile ? ChatMessageBubble : ChatMessage;

  return (
    <div className="flex flex-col gap-3 p-3">
      <MessageComponent message={userMessage} />
      {hasFailed ? (
        <div className="rounded-md border border-error bg-error-light p-3 text-sm text-error-dark">
          <p className="font-medium">L'assistant n'a pas pu répondre</p>
          <p className="mt-1 text-xs">{formatPanelError(error, t('chat_error'))}</p>
        </div>
      ) : (
        <MessageComponent message={assistantMessage} />
      )}
    </div>
  );
}
